import type {
  IAuthStore,
  IQueryClient,
  ISubscriptionStore,
  IWorkspaceStore,
  SubscriptionPlan,
  SubscriptionStatus,
  TQueryClientFactory,
} from "@workspace/types";
import { inject, injectable } from "inversify";
import { makeAutoObservable, when } from "mobx";
import {
  createCheckoutSession,
  createCustomerPortalUrl,
  getSubscriptionStatus,
  updateSubscriptionPlan,
} from "@workspace/api";
import { ServiceID } from "./symbols";

@injectable()
export class SubscriptionManager implements ISubscriptionStore {
  private statusQuery: IQueryClient<SubscriptionStatus>;
  constructor(
    @inject(ServiceID.AuthStore) private readonly auth: IAuthStore,
    @inject(ServiceID.WorkspaceStore)
    private readonly workspace: IWorkspaceStore,
    @inject(ServiceID.QueryClientFactory)
    private createQueryClient: TQueryClientFactory,
  ) {
    this.statusQuery = createQueryClient(() => ({
      queryKey: [
        "subscription-status",
        auth.user?.id,
        workspace.activeWorkspaceId,
      ],
      queryFn: () => getSubscriptionStatus(workspace.activeWorkspaceId!),
      enabled: this.auth.isAuthenticated && !!this.workspace.activeWorkspaceId,
    }));

    makeAutoObservable(this, {}, { autoBind: true });
  }

  get status() {
    return this.statusQuery.result.data || null;
  }

  get ready(): boolean {
    return this.statusQuery.result.isFetched;
  }

  get currentPlanName(): string {
    if (!this.status) {
      return "Free";
    }

    const plans: Record<SubscriptionPlan, string> = {
      free: "Free",
      team_monthly: "Team",
      team_annual: "Team",
      enterprise: "Enterprise",
    };
    return plans[this.status.plan];
  }

  async subscribe(plan: SubscriptionPlan): Promise<{ checkout_url: string }> {
    if (!this.workspace.activeWorkspaceId) {
      throw new Error("Workspace ID not found");
    }

    return createCheckoutSession(this.workspace.activeWorkspaceId, plan);
  }

  async unsubscribe(): Promise<{ portal_url: string }> {
    if (!this.workspace.activeWorkspaceId) {
      throw new Error("Workspace ID not found");
    }

    return createCustomerPortalUrl(this.workspace.activeWorkspaceId);
  }
  async updatePlan(plan: SubscriptionPlan): Promise<void> {
    if (!this.workspace.activeWorkspaceId) {
      throw new Error("Workspace ID not found");
    }

    await updateSubscriptionPlan(this.workspace.activeWorkspaceId, plan);
    this.statusQuery.refetch();
  }
}
